type ErrorLike = {message?: unknown};

export type LexicalPasteErrorDetails = {
  code: 66 | 211;
  node_key?: string;
  node_class?: string;
  node_type?: string;
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && typeof (error as ErrorLike).message === 'string') {
    return (error as ErrorLike).message as string;
  }
  return '';
};

/**
 * Extract only node metadata from the two Lexical errors involved in the paste
 * failure. The full error message is deliberately not returned or logged.
 */
export const getLexicalPasteErrorDetails = (error: unknown): LexicalPasteErrorDetails | null => {
  const message = getErrorMessage(error);
  const minifiedUrl = message.match(/lexical\.dev\/docs\/error\?([^\s]+)/)?.[1];

  if (minifiedUrl) {
    try {
      const params = new URLSearchParams(minifiedUrl.replace(/[.,;)]$/, ''));
      const code = Number(params.get('code'));
      const values = params.getAll('v').map((value) => value.slice(0, 128));

      if (code === 66) {
        return {code, ...(values[0] ? {node_key: values[0]} : {})};
      }
      if (code === 211) {
        return {
          code,
          ...(values[0] ? {node_class: values[0]} : {}),
          ...(values[1] ? {node_type: values[1]} : {}),
        };
      }
    } catch {
      // Fall through to the development-message checks below.
    }
  }

  const missingParent = message.match(/^Expected node ([^\s]+) to have a parent\.?$/);
  if (missingParent) {
    return {code: 66, node_key: missingParent[1].slice(0, 128)};
  }

  const missingBlockAncestor = message.match(
    /^Expected node ([^\s]+) of type ([^\s]+) to have a block ElementNode ancestor\.?$/
  );
  if (missingBlockAncestor) {
    return {
      code: 211,
      node_class: missingBlockAncestor[1].slice(0, 128),
      node_type: missingBlockAncestor[2].slice(0, 128),
    };
  }

  return null;
};

export const isLexicalPasteError = (error: unknown): boolean =>
  getLexicalPasteErrorDetails(error) !== null;

type SentryEventLike = {
  breadcrumbs?: Array<{category?: string; data?: Record<string, unknown>}>;
  exception?: {values?: Array<{value?: string}>};
};

/** Keep only correlated #66/#211 paste failures out of the normal 10% sample. */
export const shouldAlwaysSendLexicalPasteError = (
  event: SentryEventLike,
  originalException: unknown
): boolean => {
  const hasPasteAttempt = event.breadcrumbs?.some(
    (breadcrumb) =>
      breadcrumb.category === 'lexical.paste' && typeof breadcrumb.data?.attempt_id === 'string'
  );
  if (!hasPasteAttempt) {
    return false;
  }

  return (
    isLexicalPasteError(originalException) ||
    Boolean(event.exception?.values?.some((value) => isLexicalPasteError(value.value)))
  );
};
